import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import { codigoErroDe } from '@/components/pipeline/erros'
import { PageHeader } from '@/components/common/PageHeader'
import { EstadoDeLista, EstadoDeListaTipo } from '@/components/common/EstadoDeLista'
import { authStore } from '@/store/authStore'
import { tokens } from '@/theme'
import { rankingService } from '@/services/rankingService'
import { campanhaService } from '@/services/campanhaService'
import { equipeService } from '@/services/equipeService'
import { tenantService } from '@/services/tenantService'
import { permissoesGestaoComercial } from '@/utils/gestaoComercial'
import { CANAIS_COMERCIAIS, CANAL_LABEL, CanalComercial } from '@/types/statusReserva'
import { AUTOR_PAPEL_ROTULO, AlteracaoGestao, Campanha, rotuloAlteracao } from '@/types/campanha'
import { Equipe } from '@/types/equipe'
import { TenantAdmin } from '@/types/tenant'
import {
  ESCOPOS_RANKING,
  ESCOPO_RANKING_DESCRICAO,
  ESCOPO_RANKING_ROTULO,
  EscopoRanking,
  FAIXA_RANKING_ROTULO,
  PeriodoRanking,
  RankingAdmin,
  RankingConfiguracao,
  mensagemErroRanking,
  rotuloValorEscopo,
} from '@/types/ranking'

const formatBRL = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)

const formatDataHora = (iso?: string | null) => {
  if (!iso) return '—'
  const [y, m, d] = iso.slice(0, 10).split('-')
  return `${d}/${m}/${y}${iso.length > 15 ? ` ${iso.slice(11, 16)}` : ''}`
}

const rotuloCanal = (canal?: string | null) =>
  canal ? (CANAL_LABEL[canal as CanalComercial] ?? canal) : 'Sem canal'

const abreviar = (id: string) => (id.length > 8 ? `${id.slice(0, 8)}…` : id)

const estadoDoErro = (e: unknown): EstadoDeListaTipo =>
  (e as { status?: number })?.status === 403 ? 'semPermissao' : 'erro'

const TAMANHO_PAGINA = 20

/**
 * Ranking por volume ganho com escopo (AN-8 §5). Duas partes:
 * - **Configuração** (singleton, Q-AN8-7): o ADMIN escolhe o escopo que o consultor vê no card
 *   "Seu ranking" (aba Histórico com a trilha); o back-office (OPERADOR+) lê.
 * - **Ranking** (visão ampliada): ADMIN em qualquer escopo; GERENTE com GESTOR_EQUIPE em qualquer
 *   escopo (menos empresa), vendo só as linhas da equipe dele com a posição do universo inteiro.
 *   OPERADOR não tem visão ampliada (Q-AN8-9).
 * Posição, faixa e volume vêm prontos do backend — a tela não ordena nem calcula.
 */
const RankingPage: FC = () => {
  const usuario = authStore((s) => s.usuario)
  const perm = useMemo(() => permissoesGestaoComercial(usuario?.permissoes), [usuario])

  if (!perm.leCampanhas) {
    return (
      <Box>
        <PageHeader title="Ranking" subtitle="Ranking dos consultores por volume ganho" />
        <Paper>
          <EstadoDeLista estado="semPermissao" minHeight={240} />
        </Paper>
      </Box>
    )
  }

  return (
    <Box>
      <PageHeader title="Ranking" subtitle="Ranking dos consultores por volume ganho" />
      <Stack spacing={2}>
        <ConfiguracaoRanking podeEditar={perm.ehAdmin} />
        {perm.geriParticipantes ? (
          <RankingTabela ehAdmin={perm.ehAdmin} />
        ) : (
          <Alert severity="info" variant="outlined">
            A lista do ranking por consultor está disponível para o administrador e para o gerente
            gestor de equipe.
          </Alert>
        )}
      </Stack>
    </Box>
  )
}

// ─────────────────────────────── Configuração ───────────────────────────────

const ConfiguracaoRanking: FC<{ podeEditar: boolean }> = ({ podeEditar }) => {
  const { enqueueSnackbar } = useSnackbar()
  const [config, setConfig] = useState<RankingConfiguracao | null>(null)
  const [estado, setEstado] = useState<EstadoDeListaTipo | null>('carregando')
  const [erroMsg, setErroMsg] = useState<string | undefined>()
  const [escopo, setEscopo] = useState<EscopoRanking>('GERAL')
  const [campanhaId, setCampanhaId] = useState('')
  const [campanhas, setCampanhas] = useState<Campanha[]>([])
  const [salvando, setSalvando] = useState(false)
  const [aba, setAba] = useState<'escopo' | 'historico'>('escopo')
  const [historico, setHistorico] = useState<AlteracaoGestao[] | null>(null)
  const [estadoHist, setEstadoHist] = useState<EstadoDeListaTipo | null>(null)

  const carregar = useCallback(async () => {
    setEstado('carregando')
    setErroMsg(undefined)
    try {
      const c = await rankingService.configuracao()
      setConfig(c)
      setEscopo(c.escopo)
      setCampanhaId(c.campanhaId ?? '')
      setEstado(null)
    } catch (e) {
      setEstado(estadoDoErro(e))
      setErroMsg(codigoErroDe(e) ? mensagemErroRanking(e, 'Não foi possível carregar a configuração') : undefined)
    }
  }, [])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const carregarHistorico = useCallback(async () => {
    setEstadoHist('carregando')
    try {
      setHistorico(await rankingService.alteracoes())
      setEstadoHist(null)
    } catch (e) {
      setEstadoHist(estadoDoErro(e))
    }
  }, [])

  useEffect(() => {
    if (podeEditar && aba === 'historico' && historico === null && estadoHist === null) void carregarHistorico()
  }, [podeEditar, aba, historico, estadoHist, carregarHistorico])

  // Só campanhas ATIVAS servem de escopo (fora delas o ranking fica indisponível).
  useEffect(() => {
    if (!podeEditar || escopo !== 'CAMPANHA' || campanhas.length > 0) return
    campanhaService
      .listar(0, 100, 'ATIVA')
      .then((p) => setCampanhas(p.content ?? []))
      .catch(() => setCampanhas([]))
  }, [podeEditar, escopo, campanhas.length])

  const salvar = async () => {
    if (escopo === 'CAMPANHA' && !campanhaId) {
      enqueueSnackbar('Escolha a campanha do ranking', { variant: 'warning' })
      return
    }
    setSalvando(true)
    try {
      const c = await rankingService.salvarConfiguracao({
        escopo,
        campanhaId: escopo === 'CAMPANHA' ? campanhaId : null,
      })
      setConfig(c)
      setHistorico(null) // a troca entrou na trilha: recarrega ao abrir o Histórico
      enqueueSnackbar('Escopo do ranking atualizado', { variant: 'success' })
    } catch (e) {
      enqueueSnackbar(mensagemErroRanking(e, 'Falha ao salvar a configuração do ranking'), { variant: 'error' })
    } finally {
      setSalvando(false)
    }
  }

  const alterado =
    !!config && (config.escopo !== escopo || (config.campanhaId ?? '') !== (escopo === 'CAMPANHA' ? campanhaId : ''))

  const avisoCampanhaInativa =
    config?.escopo === 'CAMPANHA' && config.campanhaStatus && config.campanhaStatus !== 'ATIVA' ? (
      <Alert severity="warning" variant="outlined">
        A campanha do ranking está {config.campanhaStatus === 'ENCERRADA' ? 'encerrada' : 'em rascunho'}: o card “Seu
        ranking” dos consultores fica indisponível até ela estar ativa ou o escopo mudar.
      </Alert>
    ) : null

  const abaHistorico = () => {
    if (estadoHist) {
      return (
        <EstadoDeLista
          estado={estadoHist}
          minHeight={120}
          {...(estadoHist === 'erro'
            ? { acao: { label: 'Tentar novamente', onClick: () => void carregarHistorico() } }
            : {})}
        />
      )
    }
    if (!historico || historico.length === 0) {
      return <EstadoDeLista estado="vazio" mensagem="Nenhuma troca de escopo registrada" minHeight={120} />
    }
    return (
      <TableContainer>
        <Table size="small" aria-label="Histórico do escopo do ranking">
          <TableHead>
            <TableRow>
              <TableCell>Quando</TableCell>
              <TableCell>Alteração</TableCell>
              <TableCell>De → Para</TableCell>
              <TableCell>Autor</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {historico.map((h) => (
              <TableRow key={h.id}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDataHora(h.criadoEm)}</TableCell>
                <TableCell>{rotuloAlteracao(h.tipo)}</TableCell>
                <TableCell>
                  {rotuloValorEscopo(h.valorAnterior)} → {rotuloValorEscopo(h.valorNovo)}
                </TableCell>
                <TableCell>{AUTOR_PAPEL_ROTULO[h.autorPapel] ?? h.autorPapel}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )
  }

  const editor = (
    <Stack spacing={2}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <TextField
            label="Escopo"
            select
            fullWidth
            size="small"
            value={escopo}
            onChange={(e) => setEscopo(e.target.value as EscopoRanking)}
            helperText={ESCOPO_RANKING_DESCRICAO[escopo]}
          >
            {ESCOPOS_RANKING.map((e) => (
              <MenuItem key={e} value={e}>
                {ESCOPO_RANKING_ROTULO[e]}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        {escopo === 'CAMPANHA' && (
          <Grid item xs={12} md={5}>
            <TextField
              label="Campanha"
              select
              fullWidth
              size="small"
              value={campanhaId}
              onChange={(e) => setCampanhaId(e.target.value)}
              helperText="Só campanhas ativas"
            >
              {config?.campanhaId && !campanhas.some((c) => c.id === config.campanhaId) && (
                <MenuItem value={config.campanhaId}>{config.campanhaNome ?? abreviar(config.campanhaId)}</MenuItem>
              )}
              {campanhas.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.nome}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}
        <Grid item xs={12} md={3}>
          <Button
            variant="contained"
            onClick={() => void salvar()}
            disabled={salvando || !alterado}
            startIcon={salvando ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Salvar escopo
          </Button>
        </Grid>
      </Grid>
      {avisoCampanhaInativa}
      <Typography variant="caption" sx={{ color: tokens.textSecondary }}>
        Última alteração: {formatDataHora(config?.atualizadoEm)}
      </Typography>
    </Stack>
  )

  return (
    <Paper sx={{ p: 2 }} aria-label="Configuração do ranking">
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        Escopo do ranking dos consultores
      </Typography>
      <Typography variant="body2" sx={{ color: tokens.textSecondary, mb: 2 }}>
        Define com quem cada consultor é comparado no card “Seu ranking”. Vale para todos.
      </Typography>
      {podeEditar && (
        <Tabs value={aba} onChange={(_, v) => setAba(v)} sx={{ mb: 2 }}>
          <Tab value="escopo" label="Escopo" />
          <Tab value="historico" label="Histórico" />
        </Tabs>
      )}
      {podeEditar && aba === 'historico' ? (
        abaHistorico()
      ) : estado ? (
        <EstadoDeLista
          estado={estado}
          minHeight={120}
          mensagem={erroMsg}
          {...(estado === 'erro' ? { acao: { label: 'Tentar novamente', onClick: () => void carregar() } } : {})}
        />
      ) : podeEditar ? (
        editor
      ) : (
        config && (
          <Stack spacing={0.5}>
            <Typography variant="body2">
              <strong>{ESCOPO_RANKING_ROTULO[config.escopo]}</strong>
              {config.escopo === 'CAMPANHA' && config.campanhaId
                ? ` · ${config.campanhaNome ?? abreviar(config.campanhaId)}`
                : ''}
            </Typography>
            <Typography variant="caption" sx={{ color: tokens.textSecondary }}>
              {ESCOPO_RANKING_DESCRICAO[config.escopo]}
            </Typography>
            {avisoCampanhaInativa}
            <Typography variant="caption" sx={{ color: tokens.textSecondary }}>
              Última alteração: {formatDataHora(config.atualizadoEm)} · só o administrador troca o escopo
            </Typography>
          </Stack>
        )
      )}
    </Paper>
  )
}

// ─────────────────────────────── Tabela ───────────────────────────────

const mesAtual = () => new Date().toISOString().slice(0, 7)

/** Escopo da visão ampliada exige o recorte correspondente (canal, empresa, equipe, campanha). */
const recorteObrigatorio = (escopo: EscopoRanking) => escopo !== 'GERAL'

const PEDIDO_RECORTE: Record<EscopoRanking, string> = {
  GERAL: '',
  CANAL: 'o canal',
  EMPRESA: 'a empresa',
  EQUIPE: 'a equipe',
  CAMPANHA: 'a campanha',
}

const RankingTabela: FC<{ ehAdmin: boolean }> = ({ ehAdmin }) => {
  const [periodo, setPeriodo] = useState<PeriodoRanking>('MENSAL')
  const [mes, setMes] = useState(mesAtual())
  const [ano, setAno] = useState(String(new Date().getFullYear()))
  // GERENTE+GESTOR fica SEMPRE na própria equipe (AN-8 §5): o backend recusa qualquer outro escopo
  // com 403 RANKING_FORA_DA_EQUIPE, então a tela nem oferece o seletor.
  const [escopo, setEscopo] = useState<EscopoRanking>(ehAdmin ? 'GERAL' : 'EQUIPE')
  const [recorte, setRecorte] = useState('')
  const [pagina, setPagina] = useState(0)

  const [opcoesEmpresa, setOpcoesEmpresa] = useState<TenantAdmin[]>([])
  const [opcoesEquipe, setOpcoesEquipe] = useState<Equipe[]>([])
  const [opcoesCampanha, setOpcoesCampanha] = useState<Campanha[]>([])

  const [dados, setDados] = useState<RankingAdmin | null>(null)
  const [estado, setEstado] = useState<EstadoDeListaTipo | null>(null)
  const [erroMsg, setErroMsg] = useState<string | undefined>()

  // O gerente não escolhe escopo nem equipe: EQUIPE é sempre a dele.
  const precisaRecorte = (e: EscopoRanking) => ehAdmin && recorteObrigatorio(e)

  useEffect(() => {
    if (ehAdmin && escopo === 'EMPRESA' && opcoesEmpresa.length === 0)
      tenantService.listar().then(setOpcoesEmpresa).catch(() => setOpcoesEmpresa([]))
    if (ehAdmin && escopo === 'EQUIPE' && opcoesEquipe.length === 0)
      equipeService.listar(true).then(setOpcoesEquipe).catch(() => setOpcoesEquipe([]))
    if (ehAdmin && escopo === 'CAMPANHA' && opcoesCampanha.length === 0)
      campanhaService
        .listar(0, 100, 'ATIVA')
        .then((p) => setOpcoesCampanha(p.content ?? []))
        .catch(() => setOpcoesCampanha([]))
  }, [ehAdmin, escopo, opcoesEmpresa.length, opcoesEquipe.length, opcoesCampanha.length])

  const referencia = periodo === 'MENSAL' ? (mes ? `${mes}-01` : undefined) : ano ? `${ano}-01-01` : undefined
  const aguardandoRecorte = precisaRecorte(escopo) && !recorte

  const carregar = useCallback(async () => {
    if (aguardandoRecorte) {
      setDados(null)
      setEstado(null)
      return
    }
    setEstado('carregando')
    setErroMsg(undefined)
    try {
      const r = await rankingService.listar({
        periodo,
        referencia,
        escopo: ehAdmin ? escopo : 'EQUIPE',
        canal: ehAdmin && escopo === 'CANAL' ? recorte : undefined,
        tenantId: ehAdmin && escopo === 'EMPRESA' ? recorte : undefined,
        equipeId: ehAdmin && escopo === 'EQUIPE' ? recorte : undefined,
        campanhaId: ehAdmin && escopo === 'CAMPANHA' ? recorte : undefined,
        page: pagina,
        size: TAMANHO_PAGINA,
      })
      setDados(r)
      setEstado(r.linhas.length === 0 ? 'vazio' : null)
    } catch (e) {
      setDados(null)
      setEstado(estadoDoErro(e))
      setErroMsg(codigoErroDe(e) ? mensagemErroRanking(e, 'Não foi possível carregar o ranking') : undefined)
    }
  }, [aguardandoRecorte, periodo, referencia, escopo, ehAdmin, recorte, pagina])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const trocarEscopo = (e: EscopoRanking) => {
    setEscopo(e)
    setRecorte('')
    setPagina(0)
  }

  const opcoesRecorte = (): Array<{ valor: string; rotulo: string }> => {
    if (escopo === 'CANAL') return CANAIS_COMERCIAIS.map((c) => ({ valor: c, rotulo: CANAL_LABEL[c] }))
    if (escopo === 'EMPRESA') return opcoesEmpresa.map((t) => ({ valor: t.id, rotulo: t.nome }))
    if (escopo === 'EQUIPE') return opcoesEquipe.map((q) => ({ valor: q.id, rotulo: q.nome }))
    if (escopo === 'CAMPANHA') return opcoesCampanha.map((c) => ({ valor: c.id, rotulo: c.nome }))
    return []
  }

  const rotuloRecorte: Record<EscopoRanking, string> = {
    GERAL: '',
    CANAL: 'Canal',
    EMPRESA: 'Empresa',
    EQUIPE: 'Equipe',
    CAMPANHA: 'Campanha',
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
        {ehAdmin ? 'Ranking por consultor' : 'Ranking da sua equipe'}
      </Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={2}>
          <TextField
            label="Período"
            select
            size="small"
            fullWidth
            value={periodo}
            onChange={(e) => {
              setPeriodo(e.target.value as PeriodoRanking)
              setPagina(0)
            }}
          >
            <MenuItem value="MENSAL">Mensal</MenuItem>
            <MenuItem value="ANUAL">Anual</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={6} md={2}>
          {periodo === 'MENSAL' ? (
            <TextField
              label="Mês"
              type="month"
              size="small"
              fullWidth
              value={mes}
              onChange={(e) => {
                setMes(e.target.value)
                setPagina(0)
              }}
              InputLabelProps={{ shrink: true }}
            />
          ) : (
            <TextField
              label="Ano"
              type="number"
              size="small"
              fullWidth
              value={ano}
              onChange={(e) => {
                setAno(e.target.value)
                setPagina(0)
              }}
            />
          )}
        </Grid>
        {ehAdmin && (
          <Grid item xs={6} md={3}>
            <TextField
              label="Escopo da lista"
              select
              size="small"
              fullWidth
              value={escopo}
              onChange={(e) => trocarEscopo(e.target.value as EscopoRanking)}
            >
              {ESCOPOS_RANKING.map((e) => (
                <MenuItem key={e} value={e}>
                  {ESCOPO_RANKING_ROTULO[e]}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}
        {precisaRecorte(escopo) && (
          <Grid item xs={6} md={3}>
            <TextField
              label={rotuloRecorte[escopo]}
              select
              size="small"
              fullWidth
              value={recorte}
              onChange={(e) => {
                setRecorte(e.target.value)
                setPagina(0)
              }}
            >
              {opcoesRecorte().map((o) => (
                <MenuItem key={o.valor} value={o.valor}>
                  {o.rotulo}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}
      </Grid>

      {dados && (
        <Stack spacing={1} sx={{ mb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip size="small" label={dados.rotuloPeriodo} />
            <Typography variant="caption" sx={{ color: tokens.textSecondary }}>
              {ehAdmin ? `${dados.totalNoUniverso} consultor(es) no universo · ` : ''}por volume ganho
            </Typography>
          </Stack>
          {!ehAdmin && (
            <Alert severity="info" variant="outlined">
              Ranking da sua equipe: a posição e a faixa são calculadas entre os consultores ativos dela.
            </Alert>
          )}
          {!dados.disponivelParaConsultor && dados.totalNoUniverso > 0 && (
            <Typography variant="caption" sx={{ color: tokens.textSecondary }}>
              Com menos de 10 consultores no universo, o card “Seu ranking” do consultor mostra indisponível.
            </Typography>
          )}
        </Stack>
      )}

      {aguardandoRecorte ? (
        <EstadoDeLista
          estado="vazio"
          mensagem={`Escolha ${PEDIDO_RECORTE[escopo]} para ver o ranking`}
          minHeight={160}
        />
      ) : estado ? (
        <EstadoDeLista
          estado={estado}
          minHeight={160}
          mensagem={
            estado === 'vazio'
              ? dados?.recorteEquipe
                ? 'Nenhum consultor da sua equipe no ranking deste período'
                : 'Nenhum consultor no ranking deste período'
              : erroMsg
          }
          {...(estado === 'erro' ? { acao: { label: 'Tentar novamente', onClick: () => void carregar() } } : {})}
        />
      ) : (
        dados && (
          <>
            <TableContainer>
              <Table size="small" aria-label="Ranking dos consultores">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Consultor</TableCell>
                    <TableCell>Canal</TableCell>
                    <TableCell>Equipe</TableCell>
                    <TableCell align="right">Volume ganho</TableCell>
                    <TableCell align="right">Reservas</TableCell>
                    <TableCell>Faixa</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dados.linhas.map((l) => (
                    <TableRow key={l.consultorId} hover>
                      <TableCell sx={{ fontWeight: 700 }}>{l.posicao != null ? `${l.posicao}º` : '—'}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {l.nome || abreviar(l.consultorId)}
                        </Typography>
                      </TableCell>
                      <TableCell>{rotuloCanal(l.canal)}</TableCell>
                      <TableCell>{l.equipe?.nome ?? '—'}</TableCell>
                      <TableCell align="right">{formatBRL(l.volume)}</TableCell>
                      <TableCell align="right">{l.quantidade}</TableCell>
                      <TableCell>{l.faixa ? FAIXA_RANKING_ROTULO[l.faixa] : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={dados.totalLinhas}
              page={Math.min(pagina, Math.max(dados.totalPaginas - 1, 0))}
              rowsPerPage={TAMANHO_PAGINA}
              rowsPerPageOptions={[TAMANHO_PAGINA]}
              onPageChange={(_, p) => setPagina(p)}
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
            />
          </>
        )
      )}
    </Paper>
  )
}

export default RankingPage
